import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';
import {
  parteSalidaService, sucursalService, almacenService,
  tiposMovimientoService, entidadService, trabajadorService,
  tablaService, articuloService, unidadMedidaService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import ImprimirParteModal from '../components/ImprimirParteModal';
import '../styles/requerimientos.css';

const today = () => new Date().toISOString().split('T')[0];

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ESTADO_COLORS = {
  'GENERADO':       { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  'APLICADO PAR.':  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
  'APLICADO TOTAL': { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
};

export default function ParteSalida() {
  const navigate = useNavigate();
  const { sucursal: sesionSucursal } = useAuth();

  /* ── Catálogos ─────────────────────────────────────────────────── */
  const [sucursales,      setSucursales]      = useState([]);
  const [almacenes,       setAlmacenes]       = useState([]);
  const [tiposMovimiento, setTiposMovimiento] = useState([]);
  const [entidades,       setEntidades]       = useState([]);
  const [trabajadores,    setTrabajadores]    = useState([]);
  const [tiposDoc,        setTiposDoc]        = useState([]);
  const [centrosCosto,    setCentrosCosto]    = useState([]);
  const [articulos,       setArticulos]       = useState([]);
  const [unidades,        setUnidades]        = useState([]);
  const [loadingCat,      setLoadingCat]      = useState(true);

  /* ── Form ──────────────────────────────────────────────────────── */
  const [form, setForm] = useState({
    id_sucursal: '', id_almacen: '', id_tipo_movimiento: '',
    id_proveedor: '', ruc_proveedor: '', proveedor_nombre: '',
    fecha_operacion: today(),
    id_tipo_doc_ref1: '', num_doc_ref1: '',
    id_tipo_doc_ref2: '', num_doc_ref2: '',
    observacion: '', id_centro_costo: '', id_solicitante: '',
  });
  const [detalle,    setDetalle]    = useState([]);
  // Mapa { id_articulo → stock disponible } para el almacén + fecha actuales
  const [stocks,     setStocks]     = useState({});
  const [nextNumero, setNextNumero] = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [alert,       setAlert]       = useState(null);
  const [creado,      setCreado]      = useState(null);
  const [imprimiendo, setImprimiendo] = useState(false);

  /* ── Servicios estables ────────────────────────────────────────── */
  const tdSvc = useRef(tablaService('tipos-documento'));
  const ccSvc = useRef(tablaService('centros-costo'));

  /* ── Cargar catálogos ──────────────────────────────────────────── */
  const fetchCatalogos = useCallback(async () => {
    setLoadingCat(true);
    try {
      const [suc, alm, tm, ent, trab, td, cc, ums] = await Promise.all([
        sucursalService.getAll(),
        almacenService.getAll(),
        tiposMovimientoService.getAll(),
        entidadService.getAll(),
        trabajadorService.getAll(),
        tdSvc.current.getAll(),
        ccSvc.current.getAll(),
        unidadMedidaService.getAll(),
      ]);
      setSucursales(suc.data    || []);
      setAlmacenes(alm.data    || []);
      setTiposMovimiento((tm.data || []).filter(t => t.tipo === 'SALIDA'));
      setEntidades(ent.data    || []);
      setTrabajadores(trab.data || []);
      setTiposDoc(td.data      || []);
      setCentrosCosto(cc.data  || []);
      setUnidades(ums.data || []);
      // Artículos: búsqueda remota, no se precargan.
      setArticulos([]);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar catálogos.') });
    } finally {
      setLoadingCat(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalogos();
    if (sesionSucursal?.id_sucursal)
      setForm(f => ({ ...f, id_sucursal: String(sesionSucursal.id_sucursal) }));
  }, [fetchCatalogos, sesionSucursal?.id_sucursal]);

  /* ── Mapas lookup ─────────────────────────────────────────────── */
  const articuloMap = useMemo(
    () => Object.fromEntries(articulos.map(a => [String(a.id_articulo), a])),
    [articulos]
  );
  const unidadMap = useMemo(
    () => Object.fromEntries(unidades.map(u => [String(u.id_unidad), u.codigo])),
    [unidades]
  );
  const sucursalNombre = useMemo(() => {
    const s = sucursales.find(x => String(x.id_sucursal) === String(form.id_sucursal));
    return s?.nombre || '—';
  }, [sucursales, form.id_sucursal]);

  const almacenesSucursal = useMemo(
    () => sesionSucursal?.id_sucursal
      ? almacenes.filter(a => String(a.id_sucursal) === String(sesionSucursal.id_sucursal))
      : almacenes,
    [almacenes, sesionSucursal]
  );

  /* ── Tipo movimiento activo (flags obliga_*) ──────────────────── */
  const tipoMov = useMemo(
    () => tiposMovimiento.find(t => String(t.id_tipo_movimiento) === String(form.id_tipo_movimiento)) || null,
    [tiposMovimiento, form.id_tipo_movimiento]
  );

  /* ── Siguiente número al cambiar almacén ─────────────────────── */
  useEffect(() => {
    if (!form.id_almacen) { setNextNumero(null); return; }
    parteSalidaService.getNextNumero(form.id_almacen)
      .then(r => setNextNumero(r.data.numero))
      .catch(() => setNextNumero(null));
  }, [form.id_almacen]);

  /* ── Stock por artículo — cargo TODO el almacén al fijar almacén + fecha ── */
  useEffect(() => {
    if (!form.id_almacen || !form.fecha_operacion) {
      setStocks({});
      return;
    }
    let cancelled = false;
    almacenService.getStockBulk(
      Number(form.id_almacen),
      [],  // [] = todos los artículos con movimientos en el almacén
      form.fecha_operacion,
    ).then(res => {
      if (cancelled) return;
      const next = {};
      Object.entries(res.data?.stocks || {}).forEach(([k, v]) => { next[k] = parseFloat(v) || 0; });
      setStocks(next);
    }).catch(() => { /* silencioso */ });
    return () => { cancelled = true; };
  }, [form.id_almacen, form.fecha_operacion]);

  /* Enriquecer el catálogo con stock visible en el dropdown del AutoComplete */
  const articulosConStock = useMemo(() => {
    const hayAlmacenYFecha = !!form.id_almacen && !!form.fecha_operacion;
    const fmtStock = (v) => (parseFloat(v) || 0).toLocaleString('es-PE', { maximumFractionDigits: 2 });
    return articulos.map(a => {
      if (!hayAlmacenYFecha) return a;
      const s = stocks[String(a.id_articulo)];
      const stock = s === undefined ? 0 : s;
      const suffix = `  ·  Stock: ${fmtStock(stock)}${stock <= 0 ? '  ⚠' : ''}`;
      return {
        ...a,
        display_articulo: (a.display_articulo || `[${a.codigo_interno || ''}]  ${a.descripcion || ''}`) + suffix,
      };
    });
  }, [articulos, stocks, form.id_almacen, form.fecha_operacion]);

  const stockLinea = useCallback((linea) => {
    const id = linea.id_articulo;
    if (id === '' || id === null || id === undefined) return null;
    const s = stocks[String(id)];
    return s === undefined ? null : s;
  }, [stocks]);

  const lineaSuperaStock = useCallback((linea) => {
    const s = stockLinea(linea);
    if (s === null) return false;
    return (parseFloat(linea.cantidad) || 0) > s;
  }, [stockLinea]);

  const hayErrorStock = useMemo(
    () => detalle.some(l => lineaSuperaStock(l)),
    [detalle, lineaSuperaStock]
  );

  /* ── Helpers ─────────────────────────────────────────────────── */
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const req  = (flag, label) => flag ? `${label} *` : label;

  const addLinea = () => setDetalle(p => [...p, {
    _key: Date.now(),
    id_articulo: '', id_unidad: '', cantidad: 1,
    id_centro_costo: form.id_centro_costo || '',
    observacion: '',
    articulo_codigo: '', articulo_descripcion: '', unidad_descripcion: '',
  }]);

  const removeLinea = k  => setDetalle(p => p.filter(l => l._key !== k));
  const updateLinea = (k, field, val) => setDetalle(p => p.map(l => l._key === k ? { ...l, [field]: val } : l));

  const onSelectArticulo = (key, idArticulo, art) => {
    setDetalle(p => p.map(l =>
      l._key === key
        ? {
            ...l,
            id_articulo: idArticulo || '',
            id_unidad: art?.id_unidad ? String(art.id_unidad) : '',
            articulo_codigo: art?.codigo_interno || '',
            articulo_descripcion: art?.descripcion || '',
            unidad_descripcion: art?.unidad_descripcion || '',
          }
        : l
    ));
  };

  /* ── Guardar ─────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_almacen)         { setAlert({ type: 'warning', message: 'Seleccione un almacén.' }); return; }
    if (!form.id_tipo_movimiento) { setAlert({ type: 'warning', message: 'Seleccione un tipo de movimiento.' }); return; }
    if (!form.fecha_operacion)    { setAlert({ type: 'warning', message: 'Ingrese la fecha de operación.' }); return; }
    if (tipoMov) {
      if (tipoMov.obliga_ruc          && !form.id_proveedor)     { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere RUC/Proveedor.' }); return; }
      if (tipoMov.obliga_solicitante  && !form.id_solicitante)   { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Solicitante.' }); return; }
      if (tipoMov.obliga_centro_costo && !form.id_centro_costo)  { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Centro de Costo.' }); return; }
      if (tipoMov.obliga_doc_ref1     && !form.id_tipo_doc_ref1) { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Documento de Referencia 1.' }); return; }
      if (tipoMov.obliga_num_ref1     && !form.num_doc_ref1)     { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Número de Referencia 1.' }); return; }
      if (tipoMov.obliga_doc_ref2     && !form.id_tipo_doc_ref2) { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Documento de Referencia 2.' }); return; }
      if (tipoMov.obliga_num_ref2     && !form.num_doc_ref2)     { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Número de Referencia 2.' }); return; }
      if (tipoMov.obliga_glosa        && !form.observacion)      { setAlert({ type: 'warning', message: 'El tipo de movimiento requiere Glosa/Observación.' }); return; }
    }
    if (detalle.length === 0) { setAlert({ type: 'warning', message: 'Agregue al menos un artículo.' }); return; }
    if (hayErrorStock) { setAlert({ type: 'error', message: 'Hay líneas con cantidad mayor al stock disponible del almacén a la fecha.' }); return; }

    const n = v => (v !== '' && v !== null && v !== undefined) ? Number(v) : null;
    const payload = {
      id_almacen:         n(form.id_almacen),
      id_sucursal:        n(form.id_sucursal),
      id_tipo_movimiento: n(form.id_tipo_movimiento),
      id_proveedor:       n(form.id_proveedor),
      ruc_proveedor:      form.ruc_proveedor  || null,
      fecha_operacion:    form.fecha_operacion,
      id_tipo_doc_ref1:   n(form.id_tipo_doc_ref1),
      num_doc_ref1:       form.num_doc_ref1   || null,
      id_tipo_doc_ref2:   n(form.id_tipo_doc_ref2),
      num_doc_ref2:       form.num_doc_ref2   || null,
      observacion:        form.observacion    || null,
      id_centro_costo:    n(form.id_centro_costo),
      id_solicitante:     n(form.id_solicitante),
      detalle: detalle.map((l, i) => ({
        orden:           i + 1,
        id_articulo:     n(l.id_articulo),
        id_unidad:       n(l.id_unidad),
        cantidad:        parseFloat(l.cantidad) || 1,
        id_centro_costo: n(l.id_centro_costo),
        observacion:     l.observacion || null,
      })),
    };

    setSaving(true);
    try {
      const res = await parteSalidaService.create(payload);
      setCreado(res.data);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar el parte de salida.') });
    } finally {
      setSaving(false);
    }
  };

  const handleNuevo = () => {
    setCreado(null);
    setForm({
      id_sucursal: sesionSucursal?.id_sucursal ? String(sesionSucursal.id_sucursal) : '',
      id_almacen: '', id_tipo_movimiento: '', id_proveedor: '',
      ruc_proveedor: '', proveedor_nombre: '', fecha_operacion: today(),
      id_tipo_doc_ref1: '', num_doc_ref1: '', id_tipo_doc_ref2: '', num_doc_ref2: '',
      observacion: '', id_centro_costo: '', id_solicitante: '',
    });
    setDetalle([]);
    setNextNumero(null);
  };

  /* ── Pantalla de éxito ───────────────────────────────────────── */
  if (creado) {
    const ec = ESTADO_COLORS[creado.estado] || {};
    return (
      <>
      <Layout>
        <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginBottom: 8 }}>
            Parte de Salida generado
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#60a5fa', letterSpacing: 2, marginBottom: 6 }}>
            PS-{String(creado.numero).padStart(6, '0')}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 20 }}>
            {creado.almacen_nombre} · {creado.fecha_operacion?.split('T')[0] || creado.fecha_operacion}
          </div>
          <span style={{ background: ec.bg, color: ec.color, border: `1px solid ${ec.border}`, borderRadius: 12, padding: '4px 16px', fontSize: 11.5, fontWeight: 700 }}>
            {creado.estado}
          </span>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 36 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/almacen/parte-salida')}>
              Ver lista de Partes
            </button>
            <button onClick={() => setImprimiendo(true)} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '8px 18px', fontSize: 13, fontWeight: 600,
              borderRadius: 8, border: '1px solid rgba(96,165,250,0.4)', cursor: 'pointer',
              background: 'rgba(96,165,250,0.12)', color: '#60a5fa', fontFamily: 'Inter, sans-serif',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
              </svg>
              Imprimir
            </button>
            <button className="btn btn-primary" onClick={handleNuevo}>
              + Nuevo Parte de Salida
            </button>
          </div>
        </div>
      </Layout>
      {imprimiendo && (
        <ImprimirParteModal
          tipo="salida"
          parte={creado}
          trabajadores={trabajadores}
          onClose={() => setImprimiendo(false)}
        />
      )}
      </>
    );
  }

  /* ── Formulario ─────────────────────────────────────────────── */
  return (
    <Layout>
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="rq-page">

        {/* ── Header ── */}
        <div className="rq-header">
          <div className="rq-header-left">
            <div className="rq-header-title">
              <div className="rq-header-meta">Almacén</div>
              <div className="rq-header-name">Nuevo Parte de Salida</div>
            </div>
          </div>
          <div className="rq-header-actions">
            {nextNumero && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>
                  Correlativo
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#60a5fa', letterSpacing: 1.5 }}>
                  PS-{String(nextNumero).padStart(6, '0')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="rq-body">
          <form id="form-ps" onSubmit={handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0 }}>

            {/* ── SECCIÓN 1: Identificación ── */}
            <div className="rq-card">
              <div className="rq-card-title">Identificación</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.6fr 0.85fr', gap: '0 14px', alignItems: 'start' }}>

                {/* Sucursal — solo lectura */}
                <div className="form-row">
                  <label>Sucursal</label>
                  <div style={{
                    padding: '7px 12px', borderRadius: 8, fontSize: 13,
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    color: '#94a3b8', minHeight: 34, display: 'flex', alignItems: 'center',
                  }}>
                    {sucursalNombre}
                  </div>
                </div>

                <AutoComplete
                  label="Almacén *"
                  value={String(form.id_almacen || '')}
                  options={almacenesSucursal} idField="id_almacen" displayField="nombre"
                  onChange={v => setF('id_almacen', v)}
                  placeholder="Seleccionar almacén..."
                  error={!form.id_almacen}
                />
                <AutoComplete
                  label="Tipo de Movimiento *"
                  value={String(form.id_tipo_movimiento || '')}
                  options={tiposMovimiento} idField="id_tipo_movimiento"
                  displayField={t => `[${t.codigo}] ${t.descripcion}`}
                  onChange={v => setF('id_tipo_movimiento', v)}
                  placeholder="Seleccionar tipo..."
                  error={!form.id_tipo_movimiento}
                />
                <div className="form-row">
                  <label>Fecha Operación *</label>
                  <input type="date" value={form.fecha_operacion}
                    onChange={e => setF('fecha_operacion', e.target.value)} required />
                </div>
              </div>
            </div>

            {/* ── SECCIÓN 2: Proveedor/Cliente + Referencias ── */}
            <div className="rq-card">
              <div className="rq-card-title">Proveedor / Cliente · Referencias</div>
              <div className="rq-prov-obs-grid" style={{ gridTemplateColumns: '1.8fr 1fr 1.3fr 1fr 1.3fr' }}>
                <AutoComplete
                  label={req(tipoMov?.obliga_ruc, 'RUC / Proveedor-Cliente')}
                  value={String(form.id_proveedor || '')}
                  options={entidades} idField="id_entidad"
                  displayField={e => `${e.ruc || ''} — ${e.razon_social}`}
                  onChange={v => {
                    const ent = entidades.find(x => String(x.id_entidad) === String(v));
                    setForm(f => ({
                      ...f, id_proveedor: v || '',
                      ruc_proveedor: ent?.ruc || '',
                      proveedor_nombre: ent?.razon_social || '',
                    }));
                  }}
                  placeholder="Buscar por RUC o nombre..."
                  error={tipoMov?.obliga_ruc && !form.id_proveedor}
                />
                <AutoComplete
                  label={req(tipoMov?.obliga_doc_ref1, 'Doc. Ref. 1')}
                  value={String(form.id_tipo_doc_ref1 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setF('id_tipo_doc_ref1', v)}
                  placeholder="Tipo documento..."
                  error={tipoMov?.obliga_doc_ref1 && !form.id_tipo_doc_ref1}
                />
                <div className="form-row">
                  <label>{req(tipoMov?.obliga_num_ref1, 'Nº Ref. 1')}</label>
                  <input value={form.num_doc_ref1}
                    onChange={e => setF('num_doc_ref1', e.target.value)}
                    placeholder="Número referencia..."
                    style={tipoMov?.obliga_num_ref1 && !form.num_doc_ref1 ? { borderColor: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.10)' } : undefined} />
                </div>
                <AutoComplete
                  label={req(tipoMov?.obliga_doc_ref2, 'Doc. Ref. 2')}
                  value={String(form.id_tipo_doc_ref2 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setF('id_tipo_doc_ref2', v)}
                  placeholder="Tipo documento..."
                  error={tipoMov?.obliga_doc_ref2 && !form.id_tipo_doc_ref2}
                />
                <div className="form-row">
                  <label>{req(tipoMov?.obliga_num_ref2, 'Nº Ref. 2')}</label>
                  <input value={form.num_doc_ref2}
                    onChange={e => setF('num_doc_ref2', e.target.value)}
                    placeholder="Número referencia..."
                    style={tipoMov?.obliga_num_ref2 && !form.num_doc_ref2 ? { borderColor: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.10)' } : undefined} />
                </div>
              </div>
            </div>

            {/* ── SECCIÓN 3: Datos Adicionales ── */}
            <div className="rq-card">
              <div className="rq-card-title">Datos Adicionales</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0 14px', alignItems: 'stretch' }}>
                <AutoComplete
                  label={req(tipoMov?.obliga_centro_costo, 'Centro de Costo')}
                  value={String(form.id_centro_costo || '')}
                  options={centrosCosto} idField="id_centro_costo" displayField="descripcion"
                  onChange={v => setF('id_centro_costo', v)}
                  placeholder="Centro de costo..."
                  twoLine
                  error={tipoMov?.obliga_centro_costo && !form.id_centro_costo}
                />
                <AutoComplete
                  label={req(tipoMov?.obliga_solicitante, 'Solicitante')}
                  value={String(form.id_solicitante || '')}
                  options={trabajadores} idField="id_trabajador" displayField="nombres"
                  onChange={v => setF('id_solicitante', v)}
                  placeholder="Buscar trabajador..."
                  twoLine codeField="dni"
                  error={tipoMov?.obliga_solicitante && !form.id_solicitante}
                />
                <div className="form-row" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                  <label>{req(tipoMov?.obliga_glosa, 'Observaciones / Glosa')}</label>
                  <textarea value={form.observacion}
                    onChange={e => setF('observacion', e.target.value)}
                    placeholder="Observaciones del parte de salida..."
                    style={{
                      flex: 1, width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                      fontSize: 12.5, borderRadius: 8, resize: 'none', minHeight: 44,
                      background: 'rgba(255,255,255,0.06)', outline: 'none',
                      border: `1px solid ${tipoMov?.obliga_glosa && !form.observacion ? '#ef4444' : 'rgba(255,255,255,0.11)'}`,
                      boxShadow: tipoMov?.obliga_glosa && !form.observacion ? '0 0 0 3px rgba(239,68,68,0.10)' : 'none',
                      color: '#e2e8f0', fontFamily: 'Inter, sans-serif',
                      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                    onBlur={e  => {
                      e.target.style.borderColor = tipoMov?.obliga_glosa && !form.observacion ? '#ef4444' : 'rgba(255,255,255,0.11)';
                      e.target.style.boxShadow   = tipoMov?.obliga_glosa && !form.observacion ? '0 0 0 3px rgba(239,68,68,0.10)' : 'none';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ── SECCIÓN 4: Artículos ── */}
            <div className="rq-card rq-card-table">
              <div className="rq-table-header">
                <div className="rq-table-header-left">
                  <span className="rq-table-title">Artículos</span>
                  <button type="button" className="rq-btn-add-line" onClick={addLinea}>
                    + Agregar ítem
                  </button>
                </div>
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {detalle.length} ítem{detalle.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="rq-table-container">
                <table className="rq-articles">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th style={{ width: 160 }}>Artículo</th>
                      <th>Descripción</th>
                      <th style={{ width: 62, textAlign: 'center' }}>U.M.</th>
                      <th style={{ width: 86, textAlign: 'right' }}>Stock</th>
                      <th style={{ width: 88, textAlign: 'right' }}>Cantidad</th>
                      <th style={{ width: 170 }}>Centro de Costo</th>
                      <th style={{ width: 150 }}>Observación</th>
                      <th style={{ width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="rq-empty">
                          Sin artículos — use "+ Agregar ítem" para comenzar
                        </td>
                      </tr>
                    ) : detalle.map((linea, idx) => {
                      return (
                        <tr key={linea._key}>
                          <td className="rq-td-num">{idx + 1}</td>

                          {/* Artículo — autocomplete con búsqueda remota y stock del almacén seleccionado */}
                          <td className="rq-td-cod">
                            <AutoComplete
                              label="" value={String(linea.id_articulo || '')}
                              idField="id_articulo"
                              displayField={(a) => {
                                const base = `[${a.codigo_interno || ''}] ${a.descripcion || ''}${a.unidad_descripcion ? ' · ' + a.unidad_descripcion : ''}`;
                                if (!form.id_almacen || !form.fecha_operacion) return base;
                                const s = stocks[String(a.id_articulo)];
                                const stock = s === undefined ? 0 : s;
                                const fmtStock = (v) => (parseFloat(v) || 0).toLocaleString('es-PE', { maximumFractionDigits: 2 });
                                return `${base} · Stock: ${fmtStock(stock)}${stock <= 0 ? ' ⚠' : ''}`;
                              }}
                              selectedField="codigo_interno"
                              dropdownMinWidth={500}
                              asyncSearch={async (q) => { const r = await articuloService.search(q); return r.data || []; }}
                              minChars={3}
                              initialSelected={linea.id_articulo ? {
                                id_articulo: linea.id_articulo,
                                codigo_interno: linea.articulo_codigo,
                                descripcion: linea.articulo_descripcion,
                                id_unidad: linea.id_unidad,
                                unidad_descripcion: linea.unidad_descripcion,
                              } : null}
                              onChange={(v, item) => onSelectArticulo(linea._key, v, item)}
                              placeholder="Buscar (mín. 3)..."
                            />
                          </td>

                          {/* Descripción — solo lectura */}
                          <td className="rq-td-desc">
                            <span className="rq-td-text">{linea.articulo_descripcion || ''}</span>
                          </td>

                          {/* U.M. — solo lectura */}
                          <td className="rq-td-um">
                            <span className="rq-td-text">{linea.unidad_descripcion || ''}</span>
                          </td>

                          {/* Stock disponible para (almacén + fecha) */}
                          <td className="rq-td-qty" style={{ textAlign: 'right' }}>
                            {(() => {
                              const s = stockLinea(linea);
                              if (s === null) return <span style={{ color: '#475569' }}>—</span>;
                              const supera = lineaSuperaStock(linea);
                              return (
                                <span style={{
                                  color: supera ? '#f87171' : (s > 0 ? '#22c55e' : '#94a3b8'),
                                  fontWeight: 600, fontFamily: 'monospace', fontSize: 12,
                                }} title={supera ? `Supera stock (${s.toLocaleString('es-PE', { maximumFractionDigits: 4 })} disponibles)` : ''}>
                                  {s.toLocaleString('es-PE', { maximumFractionDigits: 4 })}
                                </span>
                              );
                            })()}
                          </td>

                          {/* Cantidad */}
                          <td className="rq-td-qty">
                            {(() => {
                              const supera = lineaSuperaStock(linea);
                              return (
                                <input type="number" min="0.0001" step="any"
                                  value={linea.cantidad}
                                  onChange={e => updateLinea(linea._key, 'cantidad', e.target.value)}
                                  title={supera ? 'La cantidad supera el stock disponible' : ''}
                                  style={supera ? {
                                    borderColor: '#ef4444',
                                    boxShadow: '0 0 0 2px rgba(239,68,68,0.15)',
                                    background: 'rgba(239,68,68,0.05)',
                                  } : {}}
                                />
                              );
                            })()}
                          </td>

                          {/* Centro de Costo por ítem */}
                          <td className="rq-td-cc">
                            <AutoComplete
                              label="" value={String(linea.id_centro_costo || '')}
                              options={centrosCosto} idField="id_centro_costo" displayField="descripcion"
                              onChange={v => updateLinea(linea._key, 'id_centro_costo', v)}
                              placeholder="Centro de costo..."
                              dropdownMinWidth={220}
                            />
                          </td>

                          {/* Observación por ítem */}
                          <td className="rq-td-obs">
                            <input
                              value={linea.observacion || ''}
                              onChange={e => updateLinea(linea._key, 'observacion', e.target.value)}
                              placeholder="Observación..."
                            />
                          </td>

                          <td className="rq-td-del">
                            <button type="button" className="rq-btn-del"
                              onClick={() => removeLinea(linea._key)} title="Eliminar línea">
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </form>
        </div>

        {/* ── Footer ── */}
        <div style={{
          flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.07)',
          padding: '10px 24px', display: 'flex', justifyContent: 'flex-end',
          gap: 10, background: 'rgba(0,0,0,0.18)',
        }}>
          {hayErrorStock && (
            <span style={{ alignSelf: 'center', fontSize: 12, color: '#f87171', fontWeight: 600, marginRight: 'auto' }}>
              ⚠ Hay artículos con cantidad mayor al stock disponible
            </span>
          )}
          <button type="submit" form="form-ps" className="btn btn-primary"
            disabled={saving || loadingCat || hayErrorStock}
            title={hayErrorStock ? 'Corrija las cantidades que superan el stock' : ''}
            style={{ minWidth: 150 }}>
            {saving ? 'Guardando...' : 'Guardar Parte de Salida'}
          </button>
        </div>

      </div>
    </Layout>
  );
}
